import {Card, CardContent, Stack} from '@mui/material';
import {DataGrid, GridAlignment, GridColDef} from '@mui/x-data-grid';
import {useCan} from '@refinedev/core';
import {useDataGrid} from '@refinedev/mui';
import {DateTime} from 'luxon';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {fromPhpDate} from '@app/helpers/from-php-date';
import {DeleteButton} from '@components/buttons/delete';
import {EditButton} from '@components/buttons/edit';
import {List} from '@components/crud/list';
import {Indicator} from '@pages/pilotage-rh/indicators/indicator-types';

import {IndicatorOrderDrawer} from './components/indicator-order-drawer';

const ListIndicators = () => {
  const {t} = useTranslation();
  const {dataGridProps, tableQuery} = useDataGrid<Indicator>({
    syncWithLocation: true,
    pagination: {current: 1, pageSize: 100},
    sorters: {initial: [{field: 'defaultDashboard', order: 'asc'}]},
  });
  const {rowCount, ...cleanDataGridProps} = dataGridProps;

  const {data: canEdit} = useCan({resource: 'pilotage/indicateur-tissot', action: 'edit'});
  const {data: canDelete} = useCan({resource: 'pilotage/indicateur-tissot', action: 'delete'});
  const showActionsColumn = canEdit?.can || canDelete?.can;

  const columns = useMemo<GridColDef<Indicator>[]>(
    () => [
      {
        field: 'label',
        headerName: t('pages.pilotage-rh.indicators.fields.label'),
        flex: 1,
      },
      {
        field: 'defaultDashboard',
        headerName: t('pages.pilotage-rh.indicators.fields.defaultDashboard'),
        flex: 1,
      },
      {
        field: 'active',
        headerName: t('pages.pilotage-rh.indicators.fields.active'),
        flex: 1,
        valueGetter: (value, row) => {
          return row.active ? 'Oui' : 'Non';
        },
      },
      {
        field: 'modifiedAt',
        headerName: t('pages.pilotage-rh.indicators.fields.modifiedAt'),
        flex: 1,
        renderCell: ({row}) => fromPhpDate(row.modifiedAt).toLocaleString(DateTime.DATETIME_SHORT),
      },
      ...(showActionsColumn
        ? [
            {
              field: 'actions',
              headerName: t('actions.title'),
              sortable: false,
              renderCell: function render({row}: {row: Indicator}) {
                return (
                  <Stack gap={1} direction="row">
                    <EditButton
                      hideText
                      size="small"
                      recordItemId={row.uuid}
                      accessControl={{enabled: true, hideIfUnauthorized: true}}
                    />
                    <DeleteButton
                      color="error"
                      hideText
                      size="small"
                      recordItemId={row.uuid}
                      confirmTitle={t('pages.global.user.delete.confirmTitle', {email: row.label})}
                      successNotification={{
                        message: t('pages.pilotage-rh.account.notifications.delete.success'),
                        type: 'success',
                      }}
                      accessControl={{enabled: true, hideIfUnauthorized: true}}
                    />
                  </Stack>
                );
              },
              align: 'center' as GridAlignment,
              headerAlign: 'center' as GridAlignment,
            },
          ]
        : []),
    ],
    [t, showActionsColumn],
  );

  return (
    <List>
      <IndicatorOrderDrawer indicators={[...dataGridProps.rows]} onUpdate={tableQuery.refetch} />
      <Card>
        <CardContent sx={{padding: 0}}>
          <DataGrid
            {...cleanDataGridProps}
            columns={columns}
            paginationMode={'client'}
            sortingMode={'client'}
            filterMode={'client'}
            getRowId={(row) => row.uuid}
          />
        </CardContent>
      </Card>
    </List>
  );
};

export default ListIndicators;
